import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,  // send httpOnly cookies for JWT auth
});

// Attach Bearer token from localStorage as a fallback for environments where
// 3rd-party cookies are blocked (Safari/iOS strict mode).
api.interceptors.request.use((config) => {
  const tk = localStorage.getItem('rr_access_token');
  if (tk && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${tk}`;
  }
  return config;
});

// ----- Silent refresh on 401 -----
// Many endpoints can legitimately return 401 when a short-lived access token
// expires mid-session. Instead of kicking the user out immediately, we try
// once to call POST /api/auth/refresh (which uses the long-lived refresh
// cookie). If that succeeds, we retry the original request silently.
// If it fails, only THEN do we clear the local state and bounce to /login.

const REFRESH_PATH = '/auth/refresh';
const SKIP_REFRESH_PATHS = ['/auth/login', '/auth/refresh', '/auth/logout', '/auth/bootstrap-admin'];

let refreshPromise = null;  // de-duplicate concurrent refresh attempts

async function attemptRefresh() {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    try {
      // Use a bare axios so we don't loop back through our own interceptor
      const r = await axios.post(
        `${API_BASE}${REFRESH_PATH}`,
        {},
        { withCredentials: true, headers: { 'Content-Type': 'application/json' } },
      );
      const newToken = r?.data?.access_token;
      if (newToken) {
        try { localStorage.setItem('rr_access_token', newToken); } catch (_) {}
      }
      return Boolean(newToken);
    } catch (_) {
      return false;
    } finally {
      // Reset on next tick so subsequent failures retry
      setTimeout(() => { refreshPromise = null; }, 50);
    }
  })();
  return refreshPromise;
}

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const status = err?.response?.status;
    const cfg = err?.config || {};
    const reqUrl = cfg.url || '';

    if (status === 401 && !cfg._retried && !SKIP_REFRESH_PATHS.some((p) => reqUrl.includes(p))) {
      cfg._retried = true;
      const ok = await attemptRefresh();
      if (ok) {
        // Re-attach the fresh token then replay the original request
        const tk = localStorage.getItem('rr_access_token');
        if (tk) cfg.headers = { ...(cfg.headers || {}), Authorization: `Bearer ${tk}` };
        return api(cfg);
      }
      // Refresh failed → clear local state and bounce to /login
      try { localStorage.removeItem('rr_access_token'); } catch (_) {}
      const path = window.location.pathname || '';
      if (
        !path.startsWith('/login') &&
        !path.startsWith('/portail') &&
        !path.startsWith('/signature') &&
        !path.startsWith('/mission/sign') &&
        !path.startsWith('/st-renewal') &&
        !path.startsWith('/prospect/onboarding') &&
        !path.startsWith('/preq/') &&
        !path.startsWith('/prospections/convention/sign') &&
        !path.startsWith('/sign/aoh') &&
        !path.startsWith('/sign/cadre') &&
        !path.startsWith('/book/call') &&
        !path.startsWith('/rdv/') &&
        !path.startsWith('/depot/')
      ) {
        window.location.assign('/login');
      }
    }
    return Promise.reject(err);
  }
);

/**
 * Normalise n'importe quel `error.response.data.detail` FastAPI (string,
 * dict, list, null…) en string affichable. Empêche `toast.error(obj)` de
 * passer un objet à React comme child → écran blanc.
 */
export function extractErrorMessage(err, fallback = 'Une erreur est survenue') {
  if (!err) return fallback;
  const d = err?.response?.data?.detail;
  if (d == null) return err?.message || fallback;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    // Pydantic errors → [{loc, msg, type}, ...]
    return d.map((e) => (typeof e === 'string' ? e : e?.msg || JSON.stringify(e))).join(' · ');
  }
  if (typeof d === 'object') {
    if (typeof d.message === 'string') return d.message;
    if (typeof d.detail === 'string') return d.detail;
    try { return JSON.stringify(d); } catch { return fallback; }
  }
  return String(d);
}

export default api;
